/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { Spin } from 'antd';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import timeGridPlugin from '@fullcalendar/timegrid';
import { apiGet } from '../brainApi';
import AppointmentModal from './AppointmentModal';
import { convertTime, getZ3PConfigValue } from '../helper';
import CalendarAppointment from './CalendarAppointment';
import RescheduleModal from './RescheduleModal';
import { DispatchContext, QuickAppointmentContext } from '../Context';
import { renderLegend, useCalendarStyles } from './FullCalendarHelper';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'relative',
    overflow: 'hidden',
    '& .fc-scrollgrid-section-body .fc-scroller': {
      overflow: 'scroll',
    },
    '& .fc .fc-timegrid-cols': {
      zIndex: 1,
    },
  },
  dispatchCalendar: {
    padding: 15,
    [theme.breakpoints.down('lg')]: {
      maxWidth: '100%',
    },
  },
  timezone: {
    fontStyle: 'italic',
    fontSize: 'small',
  },
}));

const COLOR_LEGEND = {
  today: {
    color: '#72bcf6',
    text: 'Today',
  },
  nonBusinessHours: {
    color: 'rgba(215, 215, 215, 0.3)',
    text: 'Outside time range offered for scheduling',
  },
};

const DispatchCalendar = ({
  date,
  selectedServiceVehicleIds,
  serviceVehicleIdToColor,
}) => {
  const { handleDate } = useContext(DispatchContext);
  const classes = useStyles();
  const calendarClasses = useCalendarStyles();
  const calendarRef = useRef(null);
  const { toggle } = useContext(QuickAppointmentContext);

  const [weeklySchedule, setWeeklySchedule] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [event, setEvent] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [scheduledWork, setScheduledWork] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const dateCopy = moment.parseZone(date).format('YYYY-MM-DD');
      const start = moment
        .parseZone(dateCopy)
        .startOf('week')
        .format('YYYY-MM-DD');
      const end = moment
        .parseZone(dateCopy)
        .endOf('week')
        .format('YYYY-MM-DD');
      const params = { start_date: start, end_date: end };
      apiGet('/get-dispatch-info', params).then((json) => {
        setWeeklySchedule(json);
      });
    }
    fetchData();
  }, []);

  const getColor = (id) => {
    return serviceVehicleIdToColor[id];
  };

  const renderEvents = () => {
    if (!weeklySchedule) {
      return [];
    }
    const schedules = weeklySchedule.schedule.filter((schedule) =>
      selectedServiceVehicleIds.includes(schedule.service_vehicle_id),
    );
    const events = [];

    schedules.forEach((schedule) => {
      const activeAppointments = schedule.appointments.filter(
        (appt) => appt.status !== 'cancelled' && appt.status !== 'suggested',
      );

      const appointments = activeAppointments.map((appointment) => ({
        id: appointment.scheduled_work_id,
        backgroundColor: getColor(schedule.service_vehicle_id),
        borderColor: getColor(schedule.service_vehicle_id),
        start: appointment.drive_start_time_utc,
        end: appointment.buffer_end_time_utc,
        title: `${appointment.customer.first_name} ${appointment.customer.last_name}`,
        display: 'auto',
      }));

      events.push(appointments);
    });
    return events.flat();
  };

  const getScheduledWorkIdFromEventId = (eventId) =>
    eventId?.substring(eventId?.lastIndexOf('-') + 1);

  const getAppointment = (id) => {
    if (!weeklySchedule || !id) {
      return null;
    }

    const schedules = [...weeklySchedule.schedule];
    const appointmentArray = [];
    schedules.forEach((schedule) =>
      appointmentArray.push(schedule.appointments),
    );

    const flattenedArray = appointmentArray.flatten();
    const selectedAppointment = flattenedArray.find(
      (appointment) => appointment.scheduled_work_id === Number(id),
    );

    return selectedAppointment;
  };

  // Format for <CalendarAppointment>
  const formatAppointment = (appointment) => {
    return {
      start_time: appointment.confirmed_appointment_start_time_utc,
      end_time: appointment.expected_appointment_end_time_utc,
      buffer_time_minutes: appointment.buffer_time_minutes,
      drive_time_minutes: appointment.drive_time_minutes,
      total_labor_hours: appointment.total_labor_hours,
      location_type: appointment.location_type,
    };
  };

  const timeRangeMin = convertTime(
    getZ3PConfigValue('time_range_min'),
    'militaryString',
  );
  const timeRangeMax = convertTime(
    getZ3PConfigValue('time_range_max'),
    'militaryString',
  );

  const timeZone = weeklySchedule?.time_zone || 'local';

  if (showRescheduleModal && scheduledWork) {
    return (
      <RescheduleModal
        open={showRescheduleModal}
        setOpen={setShowRescheduleModal}
        sw={scheduledWork}
      />
    );
  }

  if (toggle) return null;

  return (
    <div className={classes.wrapper}>
      <Spin indicator={<CircularProgress />} spinning={weeklySchedule === null}>
        <div
          className={`${classes.dispatchCalendar} ${calendarClasses.weekViewCalendar} mdCalendarWrapper`}
        >
          <FullCalendar
            eventContent={(info) => (
              <CalendarAppointment
                eventInfo={info}
                appointment={formatAppointment(getAppointment(info.event.id))}
                timeZone={weeklySchedule.time_zone}
              />
            )}
            plugins={[timeGridPlugin, momentTimezonePlugin]}
            initialView="timeGridWeek"
            timeZone={timeZone}
            ref={calendarRef}
            schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_LICENSE_KEY}
            datesAboveResources
            resourceLabelClassNames="hideResourceHeader"
            datesSet={(info) => {
              const date1 = new Date(date);
              const date2 = new Date(
                moment(info.startStr).format('YYYY-MM-DD'),
              );
              const diffTime = date2 - date1;
              const diffWeek = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
              if (diffWeek < 0) {
                handleDate(moment(date).subtract(1, 'week'));
              }
              if (diffWeek > 0) {
                handleDate(moment(date).add(1, 'week'));
              }
            }}
            headerToolbar={{
              left: 'prev',
              center: 'title',
              right: 'todayButton,next',
            }}
            views={{
              resourceTimeGridWeekCustom: {
                type: 'resourceTimeGrid',
                duration: { weeks: 1 },
              },
            }}
            customButtons={{
              todayButton: {
                text: 'Today',
                click() {
                  handleDate(moment());
                },
              },
            }}
            resourceOrder="title"
            initialDate={date}
            allDayContent={false}
            nowIndicator
            events={renderEvents()}
            eventClick={(info) => {
              setModalOpened(true);
              setEvent(info);
            }}
            businessHours={{
              startTime: timeRangeMin,
              endTime: timeRangeMax,
            }}
            contentHeight="600px"
            slotDuration="00:30:00"
            snapDuration="00:15:00"
            defaultTimedEventDuration="00:30"
            eventMinHeight={30}
            expandRows
            slotMinTime="00:00:00"
            slotLabelInterval="01:00"
            weekends
            scrollTime={timeRangeMin}
          />
          {weeklySchedule && (
            <span className={classes.timezone}>
              All times are shown in{' '}
              <strong>
                {weeklySchedule?.time_zone || 'your local time zone'}
              </strong>
              .
            </span>
          )}
          {renderLegend(COLOR_LEGEND)}
        </div>
        <AppointmentModal
          info={event}
          open={modalOpened}
          handleClose={() => setModalOpened(false)}
          appointment={getAppointment(
            getScheduledWorkIdFromEventId(event?.event.id),
          )}
          color={event?.event.backgroundColor}
          setScheduledWork={setScheduledWork}
          setShowRescheduleModal={setShowRescheduleModal}
        />
      </Spin>
    </div>
  );
};

export default DispatchCalendar;
